import {
  boardInstanceAtomFamily,
  boardUndoStackAtomFamily,
  localBoardIDsAtom,
  workingBoardInstanceAtomFamily,
} from './recoilState';
import { selectAccessTokens } from './signInRecoilState';
import { saveBoard } from 'API';
import { BoardID } from 'BoardID';
import { deleteBoardFromLS } from 'localStorage';
import { POPULAR_BOARDS_BY_NAME, POPULAR_BOARDS_IDS_SET } from 'PopularBoards';
import { useRecoilCallback } from 'recoil';
import { BoardType, RouteParams } from 'Types';
import useReactRouter from 'use-react-router';

import BoardBase from 'boardClasses/BoardBase';
import useRouter from 'utils/useRouter';

export function useCreateNewBoard(): (board: BoardBase) => void {
  return useRecoilCallback(
    ({ snapshot, reset, set }) =>
      (board) => {
        set(boardInstanceAtomFamily(board.id), board);
        reset(workingBoardInstanceAtomFamily(board.id));
        set(localBoardIDsAtom, (prev) => [...prev, board.id]);

        const accessTokens = snapshot
          .getLoadable(selectAccessTokens)
          .getValue();
        saveBoard(board, accessTokens);
      },
    []
  );
}

export function useLoadBoard(): (board: BoardBase) => void {
  return useRecoilCallback(
    ({ set }) =>
      (board: BoardBase) => {
        set(boardInstanceAtomFamily(board.id), board);
      },
    []
  );
}

export function useUpdateBoard<T extends BoardType>(): (
  board: BoardBase<T>
) => void {
  const { match, history } = useReactRouter<RouteParams>();

  return useRecoilCallback(
    ({ snapshot, reset, set }) =>
      (board) => {
        // If this is a hashed board, just replace the url
        // TODO: throttle this
        if (match.params.boardHash) {
          history.replace(`/designer/hash/${board.getHash()}`);
          return;
        }

        // If the board has returned to it's saved state, remove it from workingBoardsByID
        const savedBoard = snapshot
          .getLoadable(boardInstanceAtomFamily(board.id))
          .getValue();
        if (savedBoard && board.isEqual(savedBoard)) {
          reset(workingBoardInstanceAtomFamily(board.id));
        } else {
          set(workingBoardInstanceAtomFamily(board.id), board);

          // get the previous board state and add it to the undo stack
          const prevBoard =
            snapshot
              .getLoadable(workingBoardInstanceAtomFamily(board.id))
              .getValue() ??
            snapshot.getLoadable(boardInstanceAtomFamily(board.id)).getValue();

          if (prevBoard != null) {
            set(boardUndoStackAtomFamily(board.id), (boardUndoStack) => {
              return [...(boardUndoStack ?? []), prevBoard];
            });
          }
        }

        // If this board is not yet in the custom board list and it's not a popular board, add it
        set(localBoardIDsAtom, (prev) => {
          return prev.indexOf(board.id) === -1 &&
            !POPULAR_BOARDS_IDS_SET.has(board.id)
            ? [...prev, board.id]
            : prev;
        });
      },
    [match, history]
  );
}

export function useSaveBoard(): (board: BoardBase) => void {
  return useRecoilCallback(
    ({ snapshot, reset, set }) =>
      (board) => {
        set(boardInstanceAtomFamily(board.id), board);
        reset(workingBoardInstanceAtomFamily(board.id));

        const accessTokens = snapshot
          .getLoadable(selectAccessTokens)
          .getValue();
        saveBoard(board, accessTokens);
      },
    []
  );
}

export function useDeleteBoard(): (boardID: BoardID) => void {
  const router = useRouter();

  return useRecoilCallback(
    ({ reset, set }) =>
      (boardID) => {
        reset(boardInstanceAtomFamily(boardID));
        reset(workingBoardInstanceAtomFamily(boardID));
        set(localBoardIDsAtom, (prev) => {
          const newLocalBoardIDs = prev.filter((id) => id !== boardID);
          const boardIndex = prev.indexOf(boardID);

          const newSelectedBoardID =
            newLocalBoardIDs[boardIndex] ??
            newLocalBoardIDs[newLocalBoardIDs.length - 1] ??
            POPULAR_BOARDS_BY_NAME.checkerboard.id;

          router.navToBoardID(newSelectedBoardID);

          return newLocalBoardIDs;
        });

        deleteBoardFromLS(boardID);
      },
    []
  );
}

export function useUndo(): (boardID: BoardID) => void {
  return useRecoilCallback(
    ({ snapshot, set }) =>
      (boardID) => {
        const boardUndoStack = snapshot
          .getLoadable(boardUndoStackAtomFamily(boardID))
          .getValue();
        const prevBoard = boardUndoStack[boardUndoStack.length - 1];

        if (prevBoard == null) {
          return;
        }

        set(workingBoardInstanceAtomFamily(boardID), prevBoard);
        set(boardUndoStackAtomFamily(boardID), (stack) => stack.slice(0, -1));
      },
    []
  );
}
